import { Calendar, FileText, Flag, Package, StickyNote } from "lucide-react";
import { COLOR_AZUL_MAIN } from "../utils/const/colors-const";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { TIPO_INPUT } from "../page/viaje/tipo_input";

const PRIORIDAD_COLOR = {
  alta: "bg-red-100 text-red-700",
  media: "bg-amber-100 text-amber-700",
  baja: "bg-green-100 text-green-700",
};

const SEMANTIC_META_TYPES = new Set([
  TIPO_INPUT.GEOCERCA_ORIGEN,
  TIPO_INPUT.GEOCERCA_DESTINO,
  TIPO_INPUT.GEORUTA,
  TIPO_INPUT.FECHA_SALIDA,
  TIPO_INPUT.FECHA_LLEGADA,
  TIPO_INPUT.HORA_SALIDA,
  TIPO_INPUT.HORA_LLEGADA,
  TIPO_INPUT.SELECCIONE_VEHICULO,
]);

const parseMeta = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }
  return [];
};

const formatFecha = (iso) => {
  if (!iso) return null;
  const m = moment(iso);
  return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : null;
};

const formatMetaValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    if (typeof value.label === "string" && value.label.trim().length > 0) {
      return value.label;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }
  return String(value);
};

export default function ProgramacionSolicitudProductoKanbanCard({
  item,
  onClick,
  selected = false,
  acciones = [],
  onAccionClick,
}) {
  const { t } = useTranslation();

  const handleClick = () => {
    if (typeof onClick === "function") {
      onClick(item);
    }
  };

  const handleAccion = (accion) => {
    if (typeof onAccionClick === "function") {
      onAccionClick(accion, item);
    }
  };

  const accionesHabilitadas = (Array.isArray(acciones) ? acciones : []).filter(
    (accion) => accion?.habilitado !== 0
  );

  const detalleHabilitado = (Array.isArray(item?.detalle) ? item.detalle : [])
    .filter((row) => row?.habilitado !== 0);

  const metaCampos = parseMeta(item?.meta).filter(
    (campo) => !SEMANTIC_META_TYPES.has(campo?.type)
  );

  const prioridadKey = String(item?.prioridad ?? "").trim().toLowerCase();
  const prioridadClass = PRIORIDAD_COLOR[prioridadKey] ?? "bg-gray-100 text-gray-600";

  const fecha = formatFecha(item?.fechaProceso);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") handleClick();
      }}
      className={`w-full text-left bg-white border rounded-lg p-3 shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1 ${
        selected
          ? "border-blue-500 ring-2 ring-blue-200"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div
        className="h-1 w-10 rounded-full mb-3"
        style={{ backgroundColor: COLOR_AZUL_MAIN }}
      />

      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex items-start gap-2 min-w-0">
          <FileText className="h-4 w-4 shrink-0 mt-0.5 text-gray-500" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 leading-snug break-words">
              {item?.descripcion ?? "—"}
            </p>
            {item?.codigo ? (
              <p className="text-[11px] text-gray-500 leading-snug break-words">
                {item.codigo}
              </p>
            ) : null}
          </div>
        </div>
        {item?.prioridad ? (
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${prioridadClass}`}
          >
            <Flag className="h-3 w-3" />
            {item.prioridad}
          </span>
        ) : null}
      </div>

      {fecha ? (
        <div className="flex items-center gap-2 mt-2 min-w-0">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <p className="text-xs text-gray-500 truncate">{fecha}</p>
        </div>
      ) : null}

      {detalleHabilitado.length > 0 ? (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-1.5">
            <Package className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              {t("label_productos")} ({detalleHabilitado.length})
            </span>
          </div>
          <ul className="space-y-1">
            {detalleHabilitado.map((row) => (
              <li
                key={row.id}
                className="flex items-start justify-between gap-2 text-xs text-gray-700"
              >
                <span className="min-w-0 break-words">
                  {row.producto ?? row.idProducto ?? "—"}
                </span>
                <span className="shrink-0 font-semibold text-gray-800">
                  ×{row.cantidad ?? 0}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {metaCampos.length > 0 ? (
        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
          {metaCampos.map((campo) => (
            <div
              key={campo.id ?? campo.label}
              className="flex items-center gap-2 text-xs text-gray-600"
            >
              <span className="font-medium text-gray-500">
                {campo.label ?? "—"}:
              </span>
              <span className="min-w-0 break-words">
                {formatMetaValue(campo.value)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {item?.nota ? (
        <div className="flex items-start gap-2 mt-2 pt-2 border-t border-gray-100">
          <StickyNote className="h-3.5 w-3.5 shrink-0 mt-0.5 text-gray-400" />
          <p className="text-xs text-gray-500 break-words">{item.nota}</p>
        </div>
      ) : null}

      {accionesHabilitadas.length > 0 ? (
        <div className="flex flex-wrap justify-end gap-2 mt-3 pt-2 border-t border-gray-100">
          {accionesHabilitadas.map((accion) => (
            <button
              key={accion.id}
              type="button"
              className="inline-flex items-center justify-center min-h-8 px-3 rounded text-xs font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: accion.color || COLOR_AZUL_MAIN }}
              onClick={(event) => {
                event.stopPropagation();
                handleAccion(accion);
              }}
            >
              {accion.descripcion ?? "—"}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
